import { SubVariant } from "@/data/products";

interface VariantSwatchesProps {
  variants: SubVariant[];
  isAr: boolean;
  /** Currently selected key (colorHex or scent). */
  selectedKey?: string | null;
  /** Called when the user hovers/focuses a swatch to preview its image. */
  onPreview?: (variant: SubVariant | null) => void;
  /** Called when the user commits a selection with a click. */
  onSelect?: (variant: SubVariant) => void;
  /** How many swatches to render inline before collapsing behind "+N". */
  maxVisible?: number;
  className?: string;
}

const keyFor = (v: SubVariant) =>
  (v.colorHex ?? v.scent ?? v.poeticName ?? "").toString();

/**
 * Compact swatch strip for a product card. Colors render as filled circles,
 * scents render as small pill chips. Hover previews the variant image at the
 * parent's discretion; click commits the selection.
 */
const VariantSwatches = ({
  variants,
  isAr,
  selectedKey,
  onPreview,
  onSelect,
  maxVisible = 5,
  className = "",
}: VariantSwatchesProps) => {
  if (!variants || variants.length === 0) return null;
  const visible = variants.slice(0, maxVisible);
  const overflow = Math.max(0, variants.length - visible.length);

  return (
    <div
      className={`flex items-center gap-1.5 flex-wrap ${className}`}
      role="group"
      aria-label={isAr ? "اختر رائحة أو لون" : "Choose scent or color"}
      onMouseLeave={() => onPreview?.(null)}
    >
      {visible.map((v) => {
        const k = keyFor(v);
        const active = selectedKey === k;
        const label = isAr
          ? v.poeticNameAr || v.scentAr || v.poeticName || v.scent || ""
          : v.poeticName || v.scent || "";
        const commit = (e: React.MouseEvent | React.KeyboardEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onSelect?.(v);
        };
        if (v.colorHex) {
          return (
            <button
              key={k}
              type="button"
              aria-label={label || v.colorHex}
              aria-pressed={active}
              title={label}
              onMouseEnter={() => onPreview?.(v)}
              onFocus={() => onPreview?.(v)}
              onClick={commit}
              className={`w-5 h-5 rounded-full border transition-transform duration-200 hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
                active ? "border-primary ring-2 ring-primary/40" : "border-border/70"
              }`}
              style={{ backgroundColor: v.colorHex }}
            />
          );
        }
        return (
          <button
            key={k}
            type="button"
            aria-pressed={active}
            title={label}
            onMouseEnter={() => onPreview?.(v)}
            onFocus={() => onPreview?.(v)}
            onClick={commit}
            className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.14em] transition-colors ${
              active
                ? "border-primary bg-primary/10 text-primary"
                : "border-border/70 text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {label || "•"}
          </button>
        );
      })}
      {overflow > 0 && (
        <span
          className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
          aria-label={isAr ? `${overflow} إضافية` : `${overflow} more`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
};

export default VariantSwatches;
